import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ContentGeneration.module.scss';
import { ActionFilters, BigButton, ModalButtonArea } from '../../../common';
import Button from '../../../components/Button/Button';
import SelectCameraAnglesModal from './SelectCameraAnglesModal';
import MyRooms from '../../MyRooms';
import {
  selectGenerateVisualModal,
  setGenerateVisualModal,
} from '../../../redux/slicers/productV2Slicer';
import { useDispatch, useSelector } from 'react-redux';
import { useUserContext } from '../../../contexts/UserContext';
import { createModelAutomationRender } from '../../../helpers/jsHelper';
import { Mixpanel, MxEvent } from '../../../common/Mixpanel';
import { successToast, warningToast } from '../../../helpers/toastHelper';
import {
  apiUpdateProductV2GenerateStatus,
  apiGetProductCategoryImages,
  apiGetPropsDetail,
} from '../../../helpers/api';
import api from '../../../helpers/axiosHelper';
import { EditPensilIcon } from '../../../assets/icons';
import { STATUS_DONE_KEY } from '../../../constants/constants';
import {
  AR_ICON,
  DIMENSION_ICON,
  PRODUCT_IMAGE_ICON,
  SPINNER_ICON,
} from '../../../config/constant/assetConstant';
import { setSearchByTags, setSearchTagsOpenFrom } from '../../../redux/slicers/search/searchSlicer';

const CONTENT_TYPES = {
  imageAssets: [
    {
      id: 'productImage',
      label: 'Product Image',
      icon: PRODUCT_IMAGE_ICON,
      key: 'generate_images_content_status',
    },
    // { id: 'sideView', label: 'Side View Image', icon: '🖼️' },
    // { id: 'topView', label: 'Top View Image', icon: '📐' },
    {
      id: 'dimensionShot',
      label: 'Dimension Shot',
      icon: DIMENSION_ICON,
      key: 'generate_images_content_status',
    },
  ],
  interactive: [
    { id: '3dArViewer', label: '3D / AR Viewer', icon: AR_ICON, key: 'generate_ar_content_status' },
    {
      id: '360Viewer',
      label: '360° Viewer',
      icon: SPINNER_ICON,
      key: 'generate_360_content_status',
    },
  ],
  //   video: [{ id: 'productVideo', label: 'Product Video', icon: '🎥' }],
};

const cardProps = {
  hideFavourite: true,
  onClick: null,
  to: null,
  isCollectionItem: false,
};

const DROPDOWN_OPTIONS = [
  {
    label: 'My Library',
    value: 'myLibrary',
  },
  {
    label: 'All Templates',
    value: 'allTemplates',
  },
];

const initialValue = {
  myLibrary: { selected: {}, selectedList: [] },
  allTemplates: { selected: {}, selectedList: [] },
};

export default function ContentGeneration() {
  const dispatch = useDispatch();
  const { currentUser } = useUserContext();
  const [activeTab, setActiveTab] = useState(DROPDOWN_OPTIONS[0]);
  const [loading, setLoading] = useState(false);
  const [isFetch, setIsFetch] = useState(false);
  const [showSelected, setShowSelected] = useState(false);
  const [selectedContentTypes, setSelectedContentTypes] = useState({});
  const [imageList, setImageList] = useState({ isImage: false, isDimensionShot: false });
  // Add state to track selections per tab
  const [tabSelections, setTabSelections] = useState({ ...initialValue });
  const autoSwitch = useRef(null);
  const [productTags, setProductTags] = useState([]);
  const [showCameraPresetsModal, setShowCameraPresetsModal] = useState(false);
  const [transparentBg, setTransparentBg] = useState(false);
  const [shadowOnFloor, setShadowOnFloor] = useState(true);
  const [selectedCameraPresets, setSelectedCameraPresets] = useState({});
  const prevDimShot = useRef(false);

  // Combined unique selections from both tabs (using id for uniqueness)
  const combinedSelectedList = useMemo(() => {
    const myLibraryList = tabSelections.myLibrary?.selectedList || [];
    const allTemplatesList = tabSelections.allTemplates?.selectedList || [];
    const combined = [...myLibraryList, ...allTemplatesList];

    // Filter to keep only unique items by id
    const uniqueMap = new Map();
    combined.forEach((item) => {
      if (item?.id && !uniqueMap.has(item.id)) {
        uniqueMap.set(item.id, item);
      }
    });

    return Array.from(uniqueMap.values());
  }, [tabSelections]);

  const modelActive = useSelector(selectGenerateVisualModal);

  // Normalize modelActive to always work with arrays
  const modelActiveList = Array.isArray(modelActive)
    ? modelActive
    : modelActive
      ? [modelActive]
      : [];
  const hasActiveModels = modelActiveList.length > 0;
  const isRedirect = modelActiveList.length < 2;
  const selectedModalData = isRedirect && modelActiveList[0];

  const apiConfig = useMemo(
    () => ({
      defaultBrands: activeTab?.value === 'myLibrary' ? currentUser.organization : null,
      is_pdp_v2: true,
      ...(productTags?.length ? { tags: productTags?.join(', ') } : {}),
    }),
    [activeTab, currentUser.organization, productTags]
  );

  useEffect(() => {
    if (
      hasActiveModels &&
      selectedModalData &&
      modelActive &&
      selectedModalData?.generate_images_content_status === STATUS_DONE_KEY
    ) {
      fetchImages(selectedModalData?.id);
    }
  }, [hasActiveModels]);

  const fetchImages = async (productId) => {
    try {
      const params = {
        sort: 'is_archive,-order',
        product: productId,
        is_dimension_shot: false,
      };
      const res = await apiGetProductCategoryImages({
        id: productId,
        params,
        page: 1,
        perPage: 5,
        time: new Date()?.getTime(),
      });
      params.is_dimension_shot = true;
      const isDimensionShot = await apiGetProductCategoryImages({
        id: productId,
        params,
        page: 1,
        perPage: 5,
        time: new Date()?.getTime(),
      });
      setImageList({
        isImage: res?.results?.length,
        isDimensionShot: isDimensionShot?.results?.length,
      });
    } catch (err) {
      console.log('err RRR', err);
    }
  };

  useEffect(() => {
    if (!hasActiveModels) {
      setSelectedContentTypes({});
      setActiveTab(DROPDOWN_OPTIONS[0]);
      setLoading(false);
      setTabSelections({ ...initialValue });
      autoSwitch.current = null;
      setImageList({ isImage: false, isDimensionShot: false });
      setTransparentBg(false);
      setShadowOnFloor(true);
      setProductTags([]);
      setIsFetch(false);
      resetTags();
    }
  }, [hasActiveModels]);
  // Fetch product details and tags when modal opens
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (hasActiveModels && modelActiveList.length > 0) {
        resetTags();
        try {
          const tagsData = [];
          isFetch && setIsFetch(false);
          for (const product of modelActiveList) {
            const productId = product?.id;
            if (productId) {
              try {
                const response = await apiGetPropsDetail(productId);
                if (response?.tags) {
                  tagsData.push(...response?.tags);
                }
              } catch (error) {
                console.error(`Error fetching details for product ${productId}:`, error);
              }
            }
          }
          setProductTags(tagsData?.filter(Boolean));
        } catch (error) {
          console.error('Error fetching product details:', error);
        } finally {
          setIsFetch(true);
        }
      }
    };

    fetchProductDetails();
  }, [modelActiveList?.length]);

  const resetTags = () => {
    dispatch(setSearchByTags([]));
    dispatch(setSearchTagsOpenFrom(''));
  };

  const isDimensionSelection = selectedContentTypes?.dimensionShot;

  useEffect(() => {
    const justEnabled = isDimensionSelection && !prevDimShot.current;
    prevDimShot.current = Boolean(isDimensionSelection);
    if (justEnabled && combinedSelectedList.length > 0) {
      setShowCameraPresetsModal(true);
    }
  }, [isDimensionSelection]);

  // Reset camera angles when all templates are deselected
  useEffect(() => {
    if (combinedSelectedList.length === 0) {
      setSelectedCameraPresets({});
    }
  }, [combinedSelectedList.length]);

  if (!hasActiveModels) {
    return null;
  }

  const threeDInteraction =
    selectedContentTypes?.['3dArViewer'] || selectedContentTypes?.['360Viewer'];
  const templateSelectionRequired = selectedContentTypes?.['productImage'];
  const showTemplateSection = selectedContentTypes?.['productImage'] || selectedContentTypes?.['dimensionShot'];

  const handleEnableAndDisableBtn = () => {
    if (isDimensionSelection || threeDInteraction) {
      return false;
    }
    if (!isDimensionSelection && !threeDInteraction && !showTemplateSection) {
      return true;
    }
    return false;
  };

  const activeDisableBtn = handleEnableAndDisableBtn();

  const cardsSelected = (data) => {
    const { anySelected, allSelected, selected, selectedList } = data;
    setShowSelected(anySelected || allSelected || selectedList?.length);
    // setAllSelectedList([...selectedList]);
    // Save selections for current tab
    setTabSelections((prev) => ({
      ...prev,
      [activeTab?.value]: {
        selected: { ...selected },
        selectedList: [...selectedList],
      },
    }));
  };

  const onClose = () => {
    dispatch(setGenerateVisualModal(null));
    setShowCameraPresetsModal(false);
    setSelectedCameraPresets({});
    setTransparentBg(false);
    setShadowOnFloor(true);
    prevDimShot.current = false;
  };

  const toggleContentType = (id) => {
    setSelectedContentTypes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isAllContentTypesSelected = () => {
    const allTypes = [...CONTENT_TYPES.imageAssets, ...CONTENT_TYPES.interactive];
    return allTypes.every((item) => selectedContentTypes[item.id]);
  };

  const toggleAllContentTypes = () => {
    const allSelected = isAllContentTypesSelected();
    const updates = {};
    [...CONTENT_TYPES.imageAssets, ...CONTENT_TYPES.interactive].forEach((item) => {
      updates[item.id] = !allSelected;
    });
    setSelectedContentTypes(updates);
  };

  // Process a single item's generation
  const processGenerateForItem = async (data) => {
    const _isImageJob = templateSelectionRequired === true;
    const _isARJob = selectedContentTypes?.['3dArViewer'] === true;
    const _isViewerJob = selectedContentTypes?.['360Viewer'] === true || false;

    // If all content types are selected, make a single API call with all params
    if (isDimensionSelection && threeDInteraction && templateSelectionRequired) {
      Mixpanel.track(true, MxEvent.CLICK_GENERATE_DIMENSION);

      // Update status for all content types
      await apiUpdateProductV2GenerateStatus({
        id: data.id,
        data: {
          generate_images_content_status: 0,
          ...(_isARJob === true ? { generate_ar_content_status: 0 } : {}),
          ...(_isViewerJob === true ? { generate_360_content_status: 0 } : {}),
        },
      });

      if (combinedSelectedList?.length === 0) {
        // No templates selected — single call covering all content types via auto path
        await createModelAutomationRender({
          data,
          transparentBg,
          shadowOnFloor,
          arJob: _isARJob === true,
          viewerJob: _isViewerJob === true,
          imageJob: true,
          onlyDimensionRender: false,
          preventRedirection: !isRedirect,
          selectedListRoomList: combinedSelectedList,
        });
      } else {
        // Product image + 3D renders
        await createModelAutomationRender({
          data,
          transparentBg,
          shadowOnFloor,
          arJob: _isARJob === true,
          viewerJob: _isViewerJob === true,
          imageJob: true,
          preventRedirection: !isRedirect,
          excludeDimensionRender: true,
          selectedListRoomList: combinedSelectedList,
        });
        // Dimension shot renders — selected presets only, is_dimension_shot sent per angle
        await createModelAutomationRender({
          data,
          transparentBg,
          shadowOnFloor,
          arJob: false,
          viewerJob: false,
          imageJob: true,
          preventRedirection: !isRedirect,
          isDimensionShot: true,
          selectedListRoomList: combinedSelectedList,
          selectedCameraPresets,
        });
      }
      return 'showToast';
    }

    // Otherwise, handle each selection separately.
    // Dimension shot — no templates: auto path fetches all is_dimension_shot templates
    if (isDimensionSelection && combinedSelectedList.length === 0) {
      Mixpanel.track(true, MxEvent.CLICK_GENERATE_DIMENSION);
      await createModelAutomationRender({
        data: data,
        transparentBg,
        shadowOnFloor,
        arJob: false,
        viewerJob: false,
        imageJob: true,
        onlyDimensionRender: true,
        preventRedirection: true,
        showMessage: '',
        restrictDirection: threeDInteraction ? true : !isRedirect,
      });
    }
    // Dimension shot — templates selected: manual path, runs independently of templateSelectionRequired
    if (isDimensionSelection && combinedSelectedList.length > 0) {
      await createModelAutomationRender({
        data,
        transparentBg,
        shadowOnFloor,
        arJob: false,
        viewerJob: false,
        imageJob: true,
        preventRedirection: threeDInteraction || templateSelectionRequired ? true : !isRedirect,
        isDimensionShot: true,
        selectedListRoomList: combinedSelectedList,
        selectedCameraPresets,
      });
    }
    if (threeDInteraction) {
      if (_isARJob || _isViewerJob) {
        await apiUpdateProductV2GenerateStatus({
          id: data.id,
          data: {
            ...(_isARJob === true ? { generate_ar_content_status: 0 } : {}),
            ...(_isViewerJob === true ? { generate_360_content_status: 0 } : {}),
          },
        });
      }
      await createModelAutomationRender({
        data,
        transparentBg,
        shadowOnFloor,
        arJob: _isARJob === true,
        viewerJob: _isViewerJob === true,
        imageJob: false,
        preventRedirection: templateSelectionRequired === true && isRedirect ? true : !isRedirect,
      });
    }
    if (templateSelectionRequired === true) {
      await apiUpdateProductV2GenerateStatus({
        id: data.id,
        data: {
          generate_images_content_status: 0,
        },
      });
      // Product image renders only — dimension shot is handled in its own block above
      await createModelAutomationRender({
        data,
        transparentBg,
        shadowOnFloor,
        arJob: false,
        viewerJob: false,
        imageJob: true,
        preventRedirection: !isRedirect,
        excludeDimensionRender: true,
        selectedListRoomList: combinedSelectedList?.length ? combinedSelectedList : [],
      });
    }
    return isDimensionSelection && (threeDInteraction || templateSelectionRequired)
      ? 'showToast'
      : true;
  };
  const handleGenerate = async () => {
    if (modelActiveList.length === 0) return;

    // Warn if dimension shot is selected with templates but some templates have no camera angles
    if (isDimensionSelection && combinedSelectedList.length > 0) {
      const missingPresets = combinedSelectedList.some(
        (t) => !selectedCameraPresets[t.id]?.presetIds?.length
      );
      if (missingPresets) {
        warningToast('Please select camera angles for all templates before generating dimension shot');
        return;
      }
    }

    setLoading(true);
    let showToast = false;

    // Process each item in the array sequentially
    for (const item of modelActiveList) {
      const data = { ...item };
      showToast = await processGenerateForItem(data);
    }
    // Show success toast once after all entries are processed
    if (isDimensionSelection && showToast !== 'showToast') {
      successToast('Dimension shot creation request has been successfully submitted.');
    }
    setLoading(false);
    onClose?.();
  };

  const onSortChange = (event) => {
    setActiveTab(event);
  };

  const switchData = () => {
    if (!autoSwitch?.current) {
      setActiveTab(DROPDOWN_OPTIONS[1]);
      autoSwitch.current = true;
    }
  };

  const isVisibleTagForImage = (data) => {
    if (!data) return true;
    const isProductImage = data?.id === 'productImage';
    return Boolean(imageList[isProductImage ? 'isImage' : 'isDimensionShot']);
    // const isAvailable =  imageList?.some(({ is_dimension_shot }) =>
    //   isProductImage ? !is_dimension_shot : is_dimension_shot
    // );
    // return isAvailable
  };

  return (
    <>
      <ModalButtonArea
        size="xl"
        startOpen={hasActiveModels}
        centered={false}
        bigButton={false}
        bigIcon={true}
        variant="link"
        onClose={onClose}
        className={`${styles.contentGenerationModal} ${showTemplateSection ? styles.showSection : ''
          }`}
        backdropClassName="overlay-dark"
        headingLeft
        title={<h4>Generate Content</h4>}
        body={
          <div className={styles.dialogBody}>
            {/* Content Types Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Select Content Types to Generate</h3>
              <p className={styles.sectionSubtitle}>
                Choose a template and select which assets to generate.
              </p>

              {/* Image Assets */}
              <div className={styles.contentTypeGroup}>
                <div className={styles.groupHeader}>
                  <h4 className={styles.groupTitle}>IMAGE ASSETS</h4>
                  <div className={styles.groupToggleWrapper}>
                    <span className={styles.groupToggleLabel}>Select All</span>
                    <label className={styles.groupToggle}>
                      <input
                        type="checkbox"
                        checked={isAllContentTypesSelected()}
                        onChange={toggleAllContentTypes}
                      />
                      <span className={styles.slider}></span>
                    </label>
                  </div>
                </div>
                <div className={styles.toggleGrid}>
                  {CONTENT_TYPES.imageAssets.map((type) => {
                    const isDimShot = type.id === 'dimensionShot';
                    const showDimConfig =
                      isDimShot && isDimensionSelection && combinedSelectedList.length > 0;

                    const headerRow = (
                      <>
                        <span className={styles.toggleIcon}>
                          <img src={type.icon} alt={type?.id} />
                        </span>
                        <span className={styles.toggleLabel}>{type.label}</span>
                        {selectedModalData?.[type?.key] === STATUS_DONE_KEY &&
                          isVisibleTagForImage(type) && (
                            <span className={styles.tag}>Content Generated</span>
                          )}
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            checked={selectedContentTypes[type.id] || false}
                            onChange={() => toggleContentType(type.id)}
                          />
                          <span className={styles.slider}></span>
                        </label>
                      </>
                    );

                    if (isDimShot) {
                      const dimHeaderRow = (
                        <>
                          <span className={styles.toggleIcon}>
                            <img src={type.icon} alt={type?.id} />
                          </span>
                          <span className={styles.toggleLabel}>{type.label}</span>
                          {selectedModalData?.[type?.key] === STATUS_DONE_KEY &&
                            isVisibleTagForImage(type) && (
                              <span className={styles.tag}>Content Generated</span>
                            )}
                          {showDimConfig && (
                            <Button
                              color="link"
                              smallPadding
                              className={styles.dimEditBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCameraPresetsModal(true);
                              }}>
                              <EditPensilIcon width={12} height={12} color="currentColor" /> Edit angles
                            </Button>
                          )}
                          <label className={styles.toggle}>
                            <input
                              type="checkbox"
                              checked={selectedContentTypes[type.id] || false}
                              onChange={() => toggleContentType(type.id)}
                            />
                            <span className={styles.slider}></span>
                          </label>
                        </>
                      );
                      return (
                        <div key={type.id} className={styles.toggleItem}>
                          {dimHeaderRow}
                        </div>
                      );
                    }

                    return (
                      <div key={type.id} className={styles.toggleItem}>
                        {headerRow}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Render Options */}
              {(showTemplateSection) && (
                <div className={styles.renderOptionsRow}>
                  <span className={styles.renderOptionsLabel}>Render options</span>
                  <label className={styles.renderOptionItem}>
                    <input
                      type="checkbox"
                      checked={transparentBg}
                      onChange={() => setTransparentBg((prev) => !prev)}
                    />
                    <span>Transparent background</span>
                  </label>
                  <label className={styles.renderOptionItem}>
                    <input
                      type="checkbox"
                      checked={shadowOnFloor}
                      onChange={() => setShadowOnFloor((prev) => !prev)}
                    />
                    <span>Shadow on floor</span>
                  </label>
                </div>
              )}
              {/* 3D & Interactive and Video in same row */}
              <div className={styles.singleRow}>
                <div className={styles.contentTypeGroup}>
                  <div className={styles.groupHeader}>
                    <h4 className={styles.groupTitle}>3D & INTERACTIVE</h4>
                  </div>
                  <div className={styles.toggleGridThreeD}>
                    {CONTENT_TYPES.interactive.map((type) => (
                      <div key={type.id} className={styles.toggleItem}>
                        <span className={styles.toggleIcon}>
                          <img src={type.icon} alt={type?.id} />
                        </span>
                        <span className={styles.toggleLabel}>{type.label}</span>
                        {selectedModalData?.[type?.key] === STATUS_DONE_KEY && (
                          <span className={styles.tag}>Content Generated</span>
                        )}
                        <label className={styles.toggle}>
                          <input
                            type="checkbox"
                            checked={selectedContentTypes[type.id] || false}
                            onChange={() => toggleContentType(type.id)}
                          />
                          <span className={styles.slider}></span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* <div className={styles.contentTypeGroup}>
                <h4 className={styles.groupTitle}>VIDEO</h4>
                <div className={styles.toggleGridVideo}>
                  {CONTENT_TYPES.video.map((type) => (
                    <div key={type.id} className={styles.toggleItem}>
                      <span className={styles.toggleIcon}>
                      <img src={type.icon} alt={type?.id}/>
                      </span>
                      <span className={styles.toggleLabel}>{type.label}</span>
                      <label className={styles.toggle}>
                        <input
                          type="checkbox"
                          checked={selectedContentTypes[type.id] || false}
                          onChange={() => toggleContentType(type.id)}
                        />
                        <span className={styles.slider}></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div> */}
              </div>
            </div>

            {/* Template Selection Section */}
            <div
              className={`${styles.section} ${!showTemplateSection ? styles.hideSection : ''}`}>
              <h3 className={styles.sectionTitle}>
                Select Template{' '}
                <p className={styles.subtitle}>(Select a template to generate product image.)</p>
              </h3>
              <div
                className={`${styles.tabs} ${activeTab?.value === 'allTemplates' ? styles.templateTab : ''
                  }`}>
                <ActionFilters
                  sortProps={{
                    disabled: false,
                    value: activeTab,
                    data: DROPDOWN_OPTIONS,
                    onChange: onSortChange,
                    containerClass: 'top-filter',
                    disabledSearchable: true,
                  }}
                />
              </div>
              {/* Template Grid */}
              <div className={styles.templateGrid}>
                <span>
                  {activeTab?.value === 'myLibrary' && isFetch && (
                    <MyRooms
                      designV2
                      redirect={false}
                      singleSelect={false}
                      updatePageTitle={false}
                      showMenu={false}
                      cardProps={cardProps}
                      is360Configurator={false}
                      popupCardsSelected={(data) => cardsSelected(data)}
                      skeletonContainerClass={styles.skeletonContainerClass}
                      useGlobalFilter={false}
                      apiConfig={apiConfig}
                      switchData={switchData}
                      initialSelections={tabSelections.myLibrary}
                      useRoomSortApi={true}
                    />
                  )}
                </span>

                <span>
                  {activeTab?.value === 'allTemplates' && isFetch && (
                    <MyRooms
                      designV2
                      redirect={false}
                      singleSelect={false}
                      updatePageTitle={false}
                      showMenu={false}
                      apiConfig={apiConfig}
                      cardProps={cardProps}
                      is360Configurator={false}
                      popupCardsSelected={(data) => cardsSelected(data)}
                      skeletonContainerClass={styles.skeletonContainerClass}
                      useGlobalFilter={false}
                      initialSelections={tabSelections.allTemplates}
                      useRoomSortApi={true}
                    />
                  )}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actions}>
              <button className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <BigButton
                isLoading={loading}
                disabled={activeDisableBtn || loading}
                className={styles.generateButton}
                onClick={handleGenerate}>
                Generate Assets
              </BigButton>
            </div>
          </div>
        }
      />
      <SelectCameraAnglesModal
        key={showCameraPresetsModal ? 'open' : 'closed'}
        show={showCameraPresetsModal}
        onClose={() => setShowCameraPresetsModal(false)}
        onSave={(result) => setSelectedCameraPresets(result)}
        templates={combinedSelectedList}
        initialSelections={selectedCameraPresets}
      />
    </>
  );
}
